import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Upload, X } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useDocumentosGDB, DocumentoGDB } from "@/hooks/useDocumentosGDB";

interface SubirDocumentoGDBModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORIAS = [
  'Cartografía Base',
  'Estudios de Impacto Ambiental',
  'Planes de Manejo Ambiental',
  'Trámites ANLA',
  'Línea Base',
  'Área de Influencia',
  'Zonificación Ambiental',
  'Otros'
];

export const SubirDocumentoGDBModal: React.FC<SubirDocumentoGDBModalProps> = ({ isOpen, onClose }) => {
  const { uploadDocumento, uploading } = useDocumentosGDB();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [nombreDocumento, setNombreDocumento] = useState("");
  const [categoria, setCategoria] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [sistemaCoordenadas, setSistemaCoordenadas] = useState("");
  const [proyeccion, setProyeccion] = useState("");
  const [escala, setEscala] = useState("");
  const [fechaCaptura, setFechaCaptura] = useState<Date>();
  const [fuenteInformacion, setFuenteInformacion] = useState("");
  const [resolucionEspacial, setResolucionEspacial] = useState("");
  const [areaCobertura, setAreaCobertura] = useState("");

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!nombreDocumento) {
        setNombreDocumento(file.name.split('.')[0]);
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      if (!nombreDocumento) {
        setNombreDocumento(file.name.split('.')[0]);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile || !nombreDocumento || !categoria) {
      return;
    }

    const metadata: Partial<DocumentoGDB> = {
      descripcion,
      sistema_coordenadas: sistemaCoordenadas,
      proyeccion,
      escala,
      fecha_captura: fechaCaptura?.toISOString(),
      fuente_informacion: fuenteInformacion,
      resolucion_espacial: resolucionEspacial,
      area_cobertura: areaCobertura
    };

    const success = await uploadDocumento(selectedFile, nombreDocumento, categoria, metadata);
    
    if (success) {
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setNombreDocumento("");
    setCategoria("");
    setDescripcion("");
    setSistemaCoordenadas("");
    setProyeccion("");
    setEscala("");
    setFechaCaptura(undefined);
    setFuenteInformacion("");
    setResolucionEspacial("");
    setAreaCobertura("");
    onClose();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Subir Documento GDB</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Selección de archivo */}
          <div className="space-y-2">
            <Label>Archivo *</Label>
            {!selectedFile ? (
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer"
                onClick={() => document.getElementById('file-input')?.click()}
              >
                <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm text-muted-foreground">
                  Arrastra un archivo aquí o haz clic para seleccionar
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Formatos: .mxd, .gdb, .shp, .kml, .kmz, .mpk, .jpg, .png, .pdf, .xlsx, .dwg, .zip (Max 100 MB)
                </p>
              </div>
            ) : (
              <div className="border rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium">{selectedFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatFileSize(selectedFile.size)}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedFile(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
            <input
              id="file-input"
              type="file"
              className="hidden"
              onChange={handleFileSelect}
              accept=".mxd,.gdb,.shp,.kml,.kmz,.mpk,.jpg,.jpeg,.png,.tif,.tiff,.pdf,.xlsx,.xls,.docx,.doc,.dwg,.dxf,.zip"
            />
          </div>

          {/* Información básica */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre del Documento *</Label>
              <Input
                id="nombre"
                value={nombreDocumento}
                onChange={(e) => setNombreDocumento(e.target.value)}
                placeholder="Ej: Mapa de zonificación ambiental"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="categoria">Categoría *</Label>
              <Select value={categoria} onValueChange={setCategoria} required>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona una categoría" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIAS.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Descripción breve del documento..."
                rows={3}
              />
            </div>
          </div>

          {/* Metadatos geográficos */}
          <Accordion type="single" collapsible>
            <AccordionItem value="metadata">
              <AccordionTrigger>Metadatos Geográficos (Opcional)</AccordionTrigger>
              <AccordionContent className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sistema">Sistema de Coordenadas</Label>
                    <Input
                      id="sistema"
                      value={sistemaCoordenadas}
                      onChange={(e) => setSistemaCoordenadas(e.target.value)}
                      placeholder="Ej: MAGNA-SIRGAS"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="proyeccion">Proyección</Label>
                    <Input
                      id="proyeccion"
                      value={proyeccion}
                      onChange={(e) => setProyeccion(e.target.value)}
                      placeholder="Ej: EPSG:3116"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="escala">Escala</Label>
                    <Input
                      id="escala"
                      value={escala}
                      onChange={(e) => setEscala(e.target.value)}
                      placeholder="Ej: 1:25,000"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Fecha de Captura</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !fechaCaptura && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {fechaCaptura ? format(fechaCaptura, "PPP") : "Seleccionar fecha"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={fechaCaptura}
                          onSelect={setFechaCaptura}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="fuente">Fuente de Información</Label>
                    <Input
                      id="fuente"
                      value={fuenteInformacion}
                      onChange={(e) => setFuenteInformacion(e.target.value)}
                      placeholder="Ej: IGAC, IDEAM"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="resolucion">Resolución Espacial</Label>
                    <Input
                      id="resolucion"
                      value={resolucionEspacial}
                      onChange={(e) => setResolucionEspacial(e.target.value)}
                      placeholder="Ej: 30m/pixel"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="area">Área de Cobertura</Label>
                  <Textarea
                    id="area"
                    value={areaCobertura}
                    onChange={(e) => setAreaCobertura(e.target.value)}
                    placeholder="Ej: Municipio de Bogotá, Cundinamarca"
                    rows={2}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Botones */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={uploading || !selectedFile || !nombreDocumento || !categoria}>
              {uploading ? "Subiendo..." : "Subir Documento"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
